import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { AuthLayout } from '../components/ui/AuthLayout';
import { BrandMark } from '../components/ui/BrandMark';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { AlertCircle, ArrowForward } from '../components/ui/icons';
import { StepIndicator } from '../components/ui/StepIndicator';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Step 2 of 2: first name, last name, optional email, preferred language → PATCH /me.
 * Name fields capitalise words on mobile keyboards; the server normalises Latin names to
 * title case on save and leaves Arabic names untouched.
 */
export function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me, setMe } = useSession();
  const [firstName, setFirstName] = useState(me?.first_name ?? '');
  const [lastName, setLastName] = useState(me?.last_name ?? '');
  const [email, setEmail] = useState(me?.email ?? '');
  const [touched, setTouched] = useState(false);
  const [lang, setLang] = useState<SupportedLanguage>(
    (i18n.resolvedLanguage as SupportedLanguage | undefined) ?? 'ar',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailOk = !email.trim() || EMAIL_RE.test(email.trim());
  const valid = firstName.trim().length > 0 && lastName.trim().length > 0 && emailOk;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const { me: next } = await api.updateMe({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      setMe(next);
      void i18n.changeLanguage(lang);
      void navigate('/two', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'network');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} noValidate className="animate-rise flex flex-1 flex-col gap-6">
        <div className="pt-4 lg:pt-0">
          <StepIndicator
            label={t('onboarding.steps')}
            steps={[t('onboarding.stepAccount'), t('onboarding.stepProfile')]}
            current={1}
          />
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-balance">
            {t('onboarding.title')}
          </h1>
          <p className="mt-2 text-base text-ink-2">{t('onboarding.subtitle')}</p>
        </div>

        {/* Welcome line in TWO's voice, styled like a TWO message card. */}
        <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-sm dark:bg-elevated">
          <BrandMark size={16} className="mb-1.5" />
          <p className="text-base text-ink">{t('onboarding.welcomeLine')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('onboarding.firstName')}
            dir="auto"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            autoCapitalize="words"
            enterKeyHint="next"
            required
            error={touched && !firstName.trim() ? t('onboarding.required') : undefined}
            disabled={busy}
          />
          <Field
            label={t('onboarding.lastName')}
            dir="auto"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            autoCapitalize="words"
            enterKeyHint="next"
            required
            error={touched && !lastName.trim() ? t('onboarding.required') : undefined}
            disabled={busy}
          />
        </div>
        <Field
          label={t('onboarding.emailOptional')}
          ltr
          type="email"
          inputMode="email"
          autoComplete="email"
          enterKeyHint="done"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={touched && !emailOk ? t('onboarding.emailInvalid') : undefined}
          hint={t('onboarding.emailHint')}
          disabled={busy}
        />

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1.5 text-sm font-medium text-ink">
            {t('onboarding.preferredLanguage')}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {SUPPORTED_LANGUAGES.map((lng) => {
              const selected = lang === lng;
              return (
                <label
                  key={lng}
                  className={[
                    'flex min-h-12 cursor-pointer items-center justify-center rounded-md border text-base font-medium',
                    'transition-[border-color,background-color] duration-[var(--pa-dur-fast)]',
                    selected
                      ? 'border-brand bg-brand-soft text-ink'
                      : 'border-line-strong bg-surface text-ink-2 hover:bg-surface-2',
                    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-focus has-[:focus-visible]:outline-offset-2',
                  ].join(' ')}
                  lang={lng}
                >
                  <input
                    type="radio"
                    name="lang"
                    value={lng}
                    checked={selected}
                    onChange={() => setLang(lng)}
                    className="sr-only"
                  />
                  {t(`language.${lng}`)}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-auto flex flex-col gap-3 lg:mt-2">
          {error ? (
            <p
              role="alert"
              aria-live="assertive"
              className="flex items-start gap-2 rounded-md bg-critical-soft px-3 py-2 text-sm text-critical-text"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{t('common.error')}</span>
            </p>
          ) : null}
          <Button
            type="submit"
            size="lg"
            block
            loading={busy}
            disabled={busy || (touched && !valid)}
          >
            {t('onboarding.continue')}
            <ArrowForward size={18} />
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
